import { SaveBar as AppBridgeSaveBar } from '@shopify/app-bridge-react'
import { ContextualSaveBar } from '@shopify/polaris'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { anyWindow, useQuery } from '../common/helpers'
import { SaveBarContext, useSaveBarContext } from '../common/SaveBar'

export const SaveBarContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const query = useQuery()
  const history = useHistory()
  const location = useLocation()

  // Function used to hide the save bar and redirect with a delay,
  // since save bar is blocking history and we need to wait for it to be hidden before redirecting.
  const hideAndRedirect = useCallback(
    (url: string, method: 'push' | 'replace') => {
      setIsOpen(false)
      setTimeout(() => {
        history[method](url)
      }, 500)
    },
    [history]
  )

  // Block navigation and unblock if save bar is not open
  useEffect(() => {
    const unblock = history.block((tx: any) => {
      if (!isDirty && isOpen) {
        hideAndRedirect(`${tx.pathname}${tx.search}`, 'push')
      }

      if (isOpen) {
        return false
      }
    })

    return () => {
      unblock()
    }
  }, [isOpen, history, isDirty, hideAndRedirect])

  useEffect(() => {
    if (query.get('discard')) {
      query.delete('discard')
      setTimeout(() => {
        history.replace({
          pathname: location.pathname,
          search: query.toString(),
        })
      }, 500)
    }
  }, [query, history, location])

  return (
    <SaveBarContext.Provider value={{ isOpen, setIsOpen, isDirty, setIsDirty, hideAndRedirect }}>
      {children}
    </SaveBarContext.Provider>
  )
}

export const PolarisSaveBar = ({
  isNew,
  isDirty,
  onSave,
  onDiscard,
  isSubmitting,
  isSaveDisabled,
}: {
  isNew: boolean
  isDirty: boolean
  onSave: () => void
  onDiscard: () => void
  isSubmitting: boolean
  isSaveDisabled: boolean
}) => {
  const { isOpen, setIsOpen } = useSaveBarContext()

  useEffect(() => {
    if (isNew) {
      setIsOpen(true)
    }
  }, [setIsOpen, isNew])

  useEffect(() => {
    if (!isNew) {
      setIsOpen(isDirty)
    }
  }, [setIsOpen, isNew, isDirty])

  if (!isOpen) return null

  return (
    <ContextualSaveBar
      message="Unsaved changes"
      alignContentFlush={true}
      saveAction={{
        onAction: onSave,
        loading: isSubmitting,
        disabled: isSaveDisabled,
      }}
      discardAction={{
        onAction: onDiscard,
      }}
    />
  )
}

export const SaveBar = ({
  isNew,
  isDirty,
  onSave,
  onDiscard,
  isSubmitting,
  isSaveDisabled,
}: {
  isNew: boolean
  isDirty: boolean
  onSave: () => void
  onDiscard: () => void
  isSubmitting: boolean
  isSaveDisabled: boolean
}) => {
  const { isOpen, setIsOpen, setIsDirty } = useSaveBarContext()
  const query = useQuery()
  const history = useHistory()

  useEffect(() => {
    setIsDirty(isDirty)
  }, [isDirty, setIsDirty])

  useEffect(() => {
    if (isNew) {
      setIsOpen(true)
    }
  }, [setIsOpen, isNew])

  useEffect(() => {
    if (!isNew) {
      setIsOpen(isDirty)
    }
  }, [setIsOpen, isNew, isDirty])

  // Detect save bar hiding (including discard action button)
  // and redirect to the same page with a discard query parameter to update the history (to unblock it and allow navigation)
  useEffect(() => {
    if (!isNew && !isOpen) {
      setTimeout(() => {
        query.set('discard', 'true')
        history.replace({
          search: query.toString(),
        })
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (anyWindow.adminMode) {
    return (
      <PolarisSaveBar
        isNew={isNew}
        isDirty={isDirty}
        onSave={onSave}
        onDiscard={onDiscard}
        isSubmitting={isSubmitting}
        isSaveDisabled={isSaveDisabled}
      />
    )
  }

  return (
    <AppBridgeSaveBar open={isOpen}>
      <button
        variant="primary"
        onClick={() => onSave()}
        disabled={isSaveDisabled}
        {...(isSubmitting ? { loading: '' } : {})}
      ></button>
      <button
        onClick={() => {
          setIsOpen(false)
          onDiscard()
        }}
      >
        Discard
      </button>
    </AppBridgeSaveBar>
  )
}

export const FormikSaveBar = ({
  onDiscard,
  isNew,
}: {
  onDiscard?: () => void
  isNew?: boolean
}) => {
  const formik = useFormikContext()
  const { setIsOpen } = useSaveBarContext()

  const handleDiscard = useCallback(() => {
    formik.resetForm()
    if (onDiscard) {
      setIsOpen(false)
      onDiscard()
    }
  }, [onDiscard, formik, setIsOpen])

  return (
    <SaveBar
      isNew={!!isNew}
      isDirty={formik.dirty}
      onSave={formik.handleSubmit}
      onDiscard={handleDiscard}
      isSubmitting={formik.isSubmitting}
      isSaveDisabled={!formik.dirty}
    />
  )
}
